import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, mergeMap, tap } from 'rxjs/operators'
import { Toaster } from '@tokeet-frontend/tv3-platform'
import { EVisitorService } from './evisitor.service'
import * as EVisitorActions from './evisitor.actions'
import { LoadInquiryDetails } from '@tv3/store/inquiry/inquiry.actions'

@Injectable()
export class EVisitorEffects {
  constructor(private actions$: Actions, private evisitorService: EVisitorService, private toaster: Toaster) {}

  // Check-in to eVisitor
  checkInToEVisitor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EVisitorActions.CheckInToEVisitor),
      mergeMap(({ bookingId, payload, gatewayId, guestPkey }) =>
        this.evisitorService.checkIn(bookingId, payload, gatewayId, guestPkey).pipe(
          map((response) => EVisitorActions.CheckInToEVisitorSuccess({ bookingId, response, guestPkey })),
          catchError((error) => of(EVisitorActions.CheckInToEVisitorFailure({ bookingId, error, guestPkey })))
        )
      )
    )
  )

  checkInToEVisitorSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EVisitorActions.CheckInToEVisitorSuccess),
      tap(() => {
        this.toaster.success($localize`Guest checked in to eVisitor successfully`, $localize`Success`)
      }),
      map(({ bookingId }) => LoadInquiryDetails({ id: bookingId }))
    )
  )

  checkInToEVisitorFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EVisitorActions.CheckInToEVisitorFailure),
        tap(({ error }) => {
          const errorMessage =
            error?.error?.error || error?.error?.message || error?.message || $localize`Failed to check in guest`
          const errorCode = error?.error?.errorCode

          const friendlyMessage = errorCode ? this.evisitorService.mapErrorCodeToMessage(errorCode) : errorMessage

          this.toaster.error(friendlyMessage, $localize`eVisitor Error`)
        })
      ),
    { dispatch: false }
  )

  // Check-out from eVisitor
  checkOutFromEVisitor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EVisitorActions.CheckOutFromEVisitor),
      mergeMap(({ bookingId, referenceId, gatewayId, departure, guestPkey }) =>
        this.evisitorService.checkout(bookingId, referenceId, gatewayId, departure, guestPkey).pipe(
          map((response) => EVisitorActions.CheckOutFromEVisitorSuccess({ bookingId, response, guestPkey })),
          catchError((error) => of(EVisitorActions.CheckOutFromEVisitorFailure({ bookingId, error, guestPkey })))
        )
      )
    )
  )

  checkOutFromEVisitorSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EVisitorActions.CheckOutFromEVisitorSuccess),
      tap(() => {
        this.toaster.success($localize`Guest checked out from eVisitor successfully`, $localize`Success`)
      }),
      map(({ bookingId }) => LoadInquiryDetails({ id: bookingId }))
    )
  )

  checkOutFromEVisitorFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EVisitorActions.CheckOutFromEVisitorFailure),
        tap(({ error }) => {
          const errorMessage =
            error?.error?.error || error?.error?.message || error?.message || $localize`Failed to check out guest`
          const errorCode = error?.error?.errorCode

          const friendlyMessage = errorCode ? this.evisitorService.mapErrorCodeToMessage(errorCode) : errorMessage

          this.toaster.error(friendlyMessage, $localize`eVisitor Error`)
        })
      ),
    { dispatch: false }
  )

  // Cancel eVisitor Registration
  cancelEVisitorRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EVisitorActions.CancelEVisitorRegistration),
      mergeMap(({ bookingId, referenceId, gatewayId, guestPkey }) =>
        this.evisitorService.cancel(bookingId, referenceId, gatewayId, guestPkey).pipe(
          map((response) => EVisitorActions.CancelEVisitorRegistrationSuccess({ bookingId, response, guestPkey })),
          catchError((error) => of(EVisitorActions.CancelEVisitorRegistrationFailure({ bookingId, error, guestPkey })))
        )
      )
    )
  )

  cancelEVisitorRegistrationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EVisitorActions.CancelEVisitorRegistrationSuccess),
      tap(() => {
        this.toaster.success($localize`eVisitor registration canceled successfully`, $localize`Success`)
      }),
      map(({ bookingId }) => LoadInquiryDetails({ id: bookingId }))
    )
  )

  cancelEVisitorRegistrationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EVisitorActions.CancelEVisitorRegistrationFailure),
        tap(({ error }) => {
          const errorMessage =
            error?.error?.error ||
            error?.error?.message ||
            error?.message ||
            $localize`Failed to cancel eVisitor registration`
          const errorCode = error?.error?.errorCode

          const friendlyMessage = errorCode ? this.evisitorService.mapErrorCodeToMessage(errorCode) : errorMessage

          this.toaster.error(friendlyMessage, $localize`eVisitor Error`)
        })
      ),
    { dispatch: false }
  )
}
